import { useBillingPlan, useWallet } from "@features/billing";
import { useOrgs } from "@features/organisations";
import { formatCredits } from "@models/billing";
import BoltIcon from "@mui/icons-material/BoltRounded";
import { Box, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useOrgStore } from "@stores/orgStore";
import { useNavigate } from "@tanstack/react-router";

import { RackSegment } from "./RackSegment";

/**
 * Credits readout — denser mirror of the sidebar credits pill, sized for
 * the rack. Stays visible in rail mode where the sidebar collapses to an
 * icon strip and `SidebarCredits` is hidden behind a hover-peek; the rack
 * is the only always-on balance display on chat surfaces.
 *
 * Same data source as `SidebarCredits` (`useWallet()`); React Query
 * dedupes the request so this is a presentation-only addition. Click →
 * /settings/billing for the active scope.
 */
export function CreditsSegment() {
  const navigate = useNavigate();
  const { data: wallet } = useWallet();
  const { data: plan } = useBillingPlan();
  const activeOrgId = useOrgStore((s) => s.activeOrgId);
  const { data: orgs } = useOrgs();

  const scopeLabel = activeOrgId
    ? (orgs?.find((o) => o.id === activeOrgId)?.name ?? "Organisation")
    : "Personal";
  const isTrial = plan?.id === "trial";
  const eyebrow = `${isTrial ? "TRIAL" : "CREDITS"} · ${scopeLabel}`;

  if (!wallet) {
    return (
      <RackSegment label={eyebrow} width={200} empty>
        <Typography variant="caption" color="text.secondary">
          —
        </Typography>
      </RackSegment>
    );
  }

  const balance = wallet.balance;
  const isExhausted = balance === 0;
  const isLow = balance > 0 && balance < 200;

  return (
    <RackSegment
      label={eyebrow}
      width={200}
      onClick={() => navigate({ to: "/settings/billing" })}
    >
      <Stack direction="row" spacing={0.875} alignItems="center">
        <BoltIcon
          sx={{
            color: isExhausted
              ? "error.main"
              : isLow || isTrial
                ? "warning.main"
                : "primary.light",
            fontSize: 14,
          }}
        />
        <Typography
          sx={{
            fontFamily: (t) => t.fonts.mono,
            fontSize: 12,
            fontWeight: 600,
            color: "text.primary",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {formatCredits(balance)}
        </Typography>
        {(isTrial || isLow || isExhausted) && (
          <Box
            sx={(t) => {
              const tone = isExhausted
                ? t.palette.error.main
                : t.palette.warning.main;
              return {
                ml: 0.25,
                px: 0.625,
                py: 0.125,
                borderRadius: 0.75,
                color: tone,
                fontSize: 10,
                fontWeight: 600,
                fontFamily: t.fonts.mono,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                bgcolor: alpha(tone, 0.1),
                boxShadow: `inset 0 0 0 1px ${alpha(tone, 0.3)}`,
              };
            }}
          >
            {isExhausted ? "Empty" : isTrial ? "Trial" : "Low"}
          </Box>
        )}
      </Stack>
    </RackSegment>
  );
}
